import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useCustom, useGetLocale, useTranslate } from "@refinedev/core";
import { DateField } from "@fawkes/ui";
import {
  ArrowLeft,
  ArrowRight,
  LayoutGrid,
  List,
  PawPrint,
  RotateCcw,
  Search,
  Settings2,
  Square,
  Sun,
} from "lucide-react";

import { todayIso } from "../i18n/format";
import { useAnimalPhotoUrls } from "../api/useAnimalPhotoUrls";
import { speciesIconSrc } from "../components/AnimalOverview";
import { GroepKop, Lijst, Tegels, type Ctx } from "./pensionoverzichten/Weergave";
import { BezettingOnderdeel } from "./pensionoverzichten/Bezetting";
import {
  SORTERINGEN,
  STANDAARD_VELDEN,
  VELDEN,
  bewaarVoorkeur,
  groepeer,
  inTab,
  leesVoorkeur,
  vergelijk,
  type Groeperen,
  type Onderdeel,
  type Overzicht,
  type Regel,
  type Status,
  type Tab,
  type Veld,
  type Voorkeur,
  type Weergave,
} from "./pensionoverzichten/velden";
import "./PensionOverzichten.css";
import { PensionOnderdelenBalk, type PensionLijstPage } from "./PensionModules";

/**
 * Pensionoverzichten: welke dieren er op een dag in het pension liggen, wie er die dag komt en wie er weggaat, de
 * reserveringen van de komende dagen.
 *
 * <p>Drie weergaven — grote tegels, kleine tegels en een lijst — en de dieren los of per reservering bij elkaar.
 * Welke gegevens een tegel toont kiest de balie zelf, apart voor los en gegroepeerd: los hoort de klant in de tegel,
 * gegroepeerd staat die al in de kop van de reservering.
 *
 * <p>Wie er ligt komt van de api uit de geboekte periode, niet uit de vlaggen gekomen/opgehaald — die staan in de
 * praktijk lang niet altijd goed. Een dier telt daardoor mee op zijn aankomst- én zijn vertrekdag.
 */

const TABS: Tab[] = ["aanwezig", "aankomsten", "vertrekken", "reserveringen"];
const STATUSSEN: Status[] = ["AANWEZIG", "KOMT", "VERTREKT", "VERTROKKEN", "GEPLAND"];
/** Zo ver kijken de reserveringen vooruit. */
const VOORUIT = 14;
const GEEN_KENNEL = "__geen__";

function plusDagen(iso: string, dagen: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + dagen);
  return d.toISOString().slice(0, 10);
}

export function PensionOverzichten({
  onOpenDier,
  onOpenKlant,
  onOpenPlanning,
  onOpenLijst,
  startOnderdeel = "PENSION",
}: {
  /** Het dier openen; met klantId opent de pension het klantscherm met dit dier gekozen. */
  onOpenDier?: (dierId: number, klantId?: number | null) => void;
  /** De klant openen; met dierId staat dat dier (van de regel of reservering) meteen open. */
  onOpenKlant?: (klantId: number, dierId?: number) => void;
  onOpenPlanning?: () => void;
  /** Wegen, Bodycheck, Borstelen, Medicatie en Voerlijst staan in dezelfde balk maar zijn eigen pagina's. */
  onOpenLijst?: (page: PensionLijstPage) => void;
  startOnderdeel?: Onderdeel;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const vandaag = todayIso();
  const [onderdeel, setOnderdeel] = useState<Onderdeel>(startOnderdeel);
  const [tab, setTab] = useState<Tab>("aanwezig");
  const [datum, setDatum] = useState(vandaag);
  const [zoek, setZoek] = useState("");
  /** De diersoorten waarop de tellertegels filteren; leeg is alles. */
  const [soorten, setSoorten] = useState<Set<string>>(new Set());
  const [status, setStatus] = useState<Status | "">("");
  const [kennel, setKennel] = useState("");
  const [eigenaar, setEigenaar] = useState("");
  const [toonDagopvang, setToonDagopvang] = useState(false);
  const [voorkeur, setVoorkeurState] = useState<Voorkeur>(() => leesVoorkeur());
  const [veldenOpen, setVeldenOpen] = useState(false);
  const [dicht, setDicht] = useState<Set<string>>(new Set());

  const setVoorkeur = (v: Partial<Voorkeur>) =>
    setVoorkeurState((oud) => {
      const nieuw = { ...oud, ...v };
      bewaarVoorkeur(nieuw);
      return nieuw;
    });

  // Het tabblad Reserveringen en het onderdeel Reserveringen kijken vooruit; de rest gaat over één dag.
  const vooruit = onderdeel === "RESERVERINGEN" || (onderdeel === "PENSION" && tab === "reserveringen");
  const metDagopvang = onderdeel === "DAGOPVANG" || onderdeel === "RESERVERINGEN" || (onderdeel === "PENSION" && toonDagopvang);
  const { data, isLoading, isError } = useCustom<Overzicht>({
    url: "pension/overzicht",
    method: "get",
    config: { query: { datum, tot: vooruit ? plusDagen(datum, VOORUIT) : datum, dagopvang: metDagopvang } },
    queryOptions: { enabled: onderdeel !== "BEZETTING" },
  });
  const overzicht = data?.data;
  const alle = useMemo(() => (Array.isArray(overzicht?.regels) ? overzicht.regels : []), [overzicht]);

  // Welke dieren bij dit onderdeel horen, nog zonder filters.
  const vanOnderdeel = useMemo(() => {
    switch (onderdeel) {
      case "DAGOPVANG":
        return alle.filter((r) => r.soort === "DAGOPVANG" && r.status !== "GEPLAND");
      case "RESERVERINGEN":
        return alle.filter((r) => r.status === "GEPLAND");
      default:
        return alle.filter((r) => r.soort === "PENSION" || toonDagopvang);
    }
  }, [alle, onderdeel, toonDagopvang]);
  const actieveTab: Tab = onderdeel === "PENSION" ? tab : onderdeel === "RESERVERINGEN" ? "reserveringen" : "aanwezig";
  const inDezeTab = vanOnderdeel.filter((r) => inTab(r, actieveTab));

  const zoekterm = zoek.trim().toLocaleLowerCase("nl");
  const gefilterd = inDezeTab.filter(
    (r) =>
      (!zoekterm ||
        [r.dierNaam, r.klantNaam, r.ras, r.telefoon, r.email, r.kennel, String(r.reserveringNummer ?? "")]
          .some((x) => (x ?? "").toLocaleLowerCase("nl").includes(zoekterm))) &&
      (soorten.size === 0 || soorten.has(r.diersoort ?? "")) &&
      (!status || r.status === status) &&
      (!kennel || (kennel === GEEN_KENNEL ? r.kennel == null : r.kennel === kennel)) &&
      (!eigenaar || String(r.klantId) === eigenaar),
  );

  const groeperen = voorkeur.groeperen;
  const velden = useMemo(() => new Set<Veld>(voorkeur.velden[groeperen]), [voorkeur, groeperen]);

  const fotos = useAnimalPhotoUrls(gefilterd.map((r) => r.dierId));
  const ctx: Ctx = { t, locale, vandaag, datum, fotos, velden, gegroepeerd: groeperen === "RESERVERING", onOpenDier, onOpenKlant };

  // De keuzes in de filters komen uit wat er deze dag is, zodat er nooit een lege keuze tussen staat.
  const opties = (f: (r: Regel) => [string, string] | null) =>
    [...new Map(inDezeTab.map(f).filter((x): x is [string, string] => x != null)).entries()].sort((a, b) =>
      a[1].localeCompare(b[1], "nl", { numeric: true }),
    );
  const kennelOpties = opties((r) => (r.kennel ? [r.kennel, r.kennel] : null));
  const eigenaarOpties = opties((r) => (r.klantId != null ? [String(r.klantId), r.klantNaam ?? "—"] : null));
  const filtersAan = zoek || soorten.size > 0 || status || kennel || eigenaar;
  const wis = () => {
    setZoek("");
    setSoorten(new Set());
    setStatus("");
    setKennel("");
    setEigenaar("");
  };

  // De tellers bovenaan gaan over de dag zelf, zonder filters.
  const opDeDag = vanOnderdeel.filter((r) => r.status !== "GEPLAND");
  // Eén tegel per diersoort die er die dag is, zoals het dierenoverzicht van het asiel.
  const soortTellers = [...opDeDag.reduce((m, r) => m.set(r.diersoort ?? "", (m.get(r.diersoort ?? "") ?? 0) + 1), new Map<string, number>())]
    .filter(([naam]) => naam !== "")
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "nl"));
  const wisselSoort = (naam: string) =>
    setSoorten((oud) => {
      const nieuw = new Set(oud);
      if (nieuw.has(naam)) nieuw.delete(naam);
      else nieuw.add(naam);
      return nieuw;
    });

  const gesorteerd = [...gefilterd].sort(vergelijk(voorkeur.sortering));
  const groepen = groeperen === "RESERVERING" ? groepeer(gefilterd, voorkeur.sortering) : [];

  const inhoud = (regels: Regel[]) =>
    voorkeur.weergave === "LIJST" ? <Lijst regels={regels} ctx={ctx} /> : <Tegels regels={regels} ctx={ctx} soort={voorkeur.weergave} />;

  const titel = t(`pensionOverzichten.kop.${onderdeel}`);

  return (
    <div className="po">
      <PensionOnderdelenBalk actief={onderdeel} onOnderdeel={setOnderdeel} onLijst={(p) => onOpenLijst?.(p)} />

      {onderdeel === "PENSION" && (
        <div className="po-tabs" role="tablist" aria-label={t("pensionOverzichten.tabs")}>
          {TABS.map((x) => (
            <button key={x} type="button" role="tab" aria-selected={tab === x} data-actief={tab === x || undefined} onClick={() => setTab(x)}>
              {t(`pensionOverzichten.tab.${x}`)}
            </button>
          ))}
        </div>
      )}

      {onderdeel === "BEZETTING" ? (
        <BezettingOnderdeel t={t} locale={locale} vandaag={vandaag} onOpenPlanning={onOpenPlanning} />
      ) : (
        <>
          <header className="po-kop">
            <span className="po-kop__icoon" aria-hidden="true">
              {onderdeel === "DAGOPVANG" ? <Sun size={26} /> : <PawPrint size={26} />}
            </span>
            <div className="po-kop__tekst">
              <h1>{titel}</h1>
              <p>{t(`pensionOverzichten.ondertitel.${onderdeel}`)}</p>
            </div>
            <div className="da-overview__stats po-tellers" role="group" aria-label={t("pensionOverzichten.soort")}>
              {soortTellers.map(([naam, aantal]) => (
                <Teller
                  key={naam}
                  icoon={<img src={speciesIconSrc(naam)} alt="" />}
                  waarde={aantal}
                  label={naam}
                  actief={soorten.has(naam)}
                  onClick={() => wisselSoort(naam)}
                />
              ))}
              {onderdeel === "PENSION" && (
                <>
                  <Teller
                    icoon={<ArrowLeft size={20} />}
                    toon="groen"
                    waarde={opDeDag.filter((r) => r.aankomst).length}
                    label={t("pensionOverzichten.teller.aankomst")}
                    actief={tab === "aankomsten"}
                    onClick={() => setTab(tab === "aankomsten" ? "aanwezig" : "aankomsten")}
                  />
                  <Teller
                    icoon={<ArrowRight size={20} />}
                    toon="rood"
                    waarde={opDeDag.filter((r) => r.vertrek).length}
                    label={t("pensionOverzichten.teller.vertrek")}
                    actief={tab === "vertrekken"}
                    onClick={() => setTab(tab === "vertrekken" ? "aanwezig" : "vertrekken")}
                  />
                </>
              )}
              <Teller
                icoon={<PawPrint size={20} />}
                waarde={opDeDag.length}
                label={t("pensionOverzichten.teller.totaal")}
                actief={soorten.size === 0}
                onClick={() => setSoorten(new Set())}
              />
            </div>
          </header>

          <section className="po-filters" aria-label={t("pensionOverzichten.filters")}>
            <div className="po-filters__rij">
              <label className="po-zoek">
                <Search size={16} aria-hidden="true" />
                <input
                  type="search"
                  autoComplete="off"
                  value={zoek}
                  placeholder={t("pensionOverzichten.zoek")}
                  aria-label={t("pensionOverzichten.zoek")}
                  onChange={(e) => setZoek(e.target.value)}
                />
              </label>
              <label className="po-veld">
                <span>{t("pensionOverzichten.datum")}</span>
                <DateField value={datum} aria-label={t("pensionOverzichten.datum")} onChange={(e) => e.target.value && setDatum(e.target.value)} />
              </label>
              <Keuze
                label={t("pensionOverzichten.veld.status")}
                waarde={status}
                onChange={(v) => setStatus(v as Status | "")}
                alle={t("pensionOverzichten.alle")}
                opties={STATUSSEN.map((s) => [s, t(`pensionOverzichten.status.${s}`)])}
              />
              {onderdeel !== "DAGOPVANG" && (
                <Keuze
                  label={t("pensionOverzichten.veld.kennel")}
                  waarde={kennel}
                  onChange={setKennel}
                  alle={t("pensionOverzichten.alle")}
                  opties={[...kennelOpties, [GEEN_KENNEL, t("pensionOverzichten.nietIngedeeld")]]}
                />
              )}
              <Keuze label={t("pensionOverzichten.veld.eigenaar")} waarde={eigenaar} onChange={setEigenaar} alle={t("pensionOverzichten.alle")} opties={eigenaarOpties} />
              <button type="button" className="po-knop" onClick={wis} disabled={!filtersAan}>
                <RotateCcw size={15} aria-hidden="true" />
                {t("pensionOverzichten.wis")}
              </button>
            </div>
            <div className="po-filters__rij po-filters__rij--tweede">
              {onderdeel === "PENSION" && (
                <label className="po-schakelaar">
                  <input type="checkbox" role="switch" checked={toonDagopvang} onChange={(e) => setToonDagopvang(e.target.checked)} />
                  <span className="po-schakelaar__baan" aria-hidden="true" />
                  <strong>{t("pensionOverzichten.toonDagopvang")}</strong>
                  <span className="po-zacht">{t("pensionOverzichten.toonDagopvang.uitleg")}</span>
                </label>
              )}
              <span className="po-vul" />
              <span className="po-groepkeuze">
                <span className="po-zacht">{t("pensionOverzichten.groeperen")}</span>
                <span className="po-segment" role="group" aria-label={t("pensionOverzichten.groeperen")}>
                  {(["GEEN", "RESERVERING"] as Groeperen[]).map((g) => (
                    <button key={g} type="button" aria-pressed={groeperen === g} onClick={() => setVoorkeur({ groeperen: g })}>
                      {t(`pensionOverzichten.groeperen.${g}`)}
                    </button>
                  ))}
                </span>
              </span>
              <span className="po-segment po-segment--iconen" role="group" aria-label={t("pensionOverzichten.weergave")}>
                {(["GROOT", "KLEIN", "LIJST"] as Weergave[]).map((w) => {
                  const Icoon = w === "GROOT" ? Square : w === "KLEIN" ? LayoutGrid : List;
                  return (
                    <button
                      key={w}
                      type="button"
                      aria-pressed={voorkeur.weergave === w}
                      aria-label={t(`pensionOverzichten.weergave.${w}`)}
                      title={t(`pensionOverzichten.weergave.${w}`)}
                      onClick={() => setVoorkeur({ weergave: w })}
                    >
                      <Icoon size={16} aria-hidden="true" />
                    </button>
                  );
                })}
              </span>
              <VeldenKeuze
                t={t}
                open={veldenOpen}
                setOpen={setVeldenOpen}
                groeperen={groeperen}
                gekozen={voorkeur.velden[groeperen]}
                onChange={(lijst) => setVoorkeur({ velden: { ...voorkeur.velden, [groeperen]: lijst } })}
              />
              <label className="po-sorteer">
                <span className="po-zacht">{t("pensionOverzichten.sorteren")}</span>
                <select value={voorkeur.sortering} onChange={(e) => setVoorkeur({ sortering: e.target.value as Voorkeur["sortering"] })}>
                  {SORTERINGEN.map((s) => (
                    <option key={s} value={s}>
                      {t(`pensionOverzichten.sortering.${s}`)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </section>

          {isError ? (
            <p className="po-leeg">{t("pensionOverzichten.loadFailed")}</p>
          ) : isLoading ? (
            <p className="po-leeg">{t("common.loading")}</p>
          ) : gefilterd.length === 0 ? (
            <p className="po-leeg">
              <PawPrint size={26} aria-hidden="true" />
              {filtersAan ? t("pensionOverzichten.leeg.filters") : t(`pensionOverzichten.leeg.${onderdeel === "PENSION" ? actieveTab : onderdeel}`)}
            </p>
          ) : groeperen === "RESERVERING" ? (
            <>
              <h2 className="po-aantal">{t(`pensionOverzichten.gevonden.reserveringen.${groepen.length === 1 ? "een" : "meer"}`, { args: [groepen.length] })}</h2>
              {groepen.map((g) => {
                const open = !dicht.has(g.sleutel);
                return (
                  <section key={g.sleutel} className="po-groep" data-soort={g.regels[0].soort} aria-label={g.regels[0].klantNaam ?? undefined}>
                    <GroepKop
                      regels={g.regels}
                      ctx={ctx}
                      open={open}
                      onOpen={() =>
                        setDicht((d) => {
                          const n = new Set(d);
                          if (n.has(g.sleutel)) n.delete(g.sleutel);
                          else n.add(g.sleutel);
                          return n;
                        })
                      }
                    />
                    {open && inhoud(g.regels)}
                  </section>
                );
              })}
            </>
          ) : (
            <>
              <h2 className="po-aantal">{t(`pensionOverzichten.gevonden.dieren.${gesorteerd.length === 1 ? "een" : "meer"}`, { args: [gesorteerd.length] })}</h2>
              <div className="po-los">{inhoud(gesorteerd)}</div>
            </>
          )}
        </>
      )}
    </div>
  );
}

/** Een tellertegel zoals in het dierenoverzicht van het asiel; aanklikken filtert. */
function Teller({
  icoon,
  waarde,
  label,
  toon,
  actief,
  onClick,
}: {
  icoon: ReactNode;
  waarde: number;
  label: string;
  toon?: "groen" | "rood";
  actief?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`da-overview__stat${actief ? " da-overview__stat--active" : ""}`}
      data-toon={toon}
      aria-pressed={!!actief}
      onClick={onClick}
    >
      <span className="da-overview__stat-icon" aria-hidden="true">
        {icoon}
      </span>
      <span className="da-overview__stat-body">
        <span className="da-overview__stat-value">{waarde}</span>
        <span className="da-overview__stat-label">{label}</span>
      </span>
    </button>
  );
}

function Keuze({
  label,
  waarde,
  onChange,
  alle,
  opties,
}: {
  label: string;
  waarde: string;
  onChange: (v: string) => void;
  alle: string;
  opties: Array<[string, string]>;
}) {
  return (
    <label className="po-veld">
      <span>{label}</span>
      <select value={waarde} onChange={(e) => onChange(e.target.value)}>
        <option value="">{alle}</option>
        {opties.map(([v, tekst]) => (
          <option key={v} value={v}>
            {tekst}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Het tandwiel: welke gegevens er op de tegel staan, apart voor los en gegroepeerd. */
function VeldenKeuze({
  t,
  open,
  setOpen,
  groeperen,
  gekozen,
  onChange,
}: {
  t: (k: string, o?: { args?: unknown[] }) => string;
  open: boolean;
  setOpen: (v: boolean) => void;
  groeperen: Groeperen;
  gekozen: Veld[];
  onChange: (lijst: Veld[]) => void;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const weg = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", weg);
    return () => document.removeEventListener("mousedown", weg);
  }, [open, setOpen]);
  const aan = new Set(gekozen);
  const zet = (v: Veld, ja: boolean) => onChange(VELDEN.filter((x) => (x === v ? ja : aan.has(x))));
  return (
    <span className="po-velden" ref={ref}>
      <button type="button" className="po-knop" aria-expanded={open} onClick={() => setOpen(!open)}>
        <Settings2 size={15} aria-hidden="true" />
        {t("pensionOverzichten.velden")}
      </button>
      {open && (
        <div className="po-velden__paneel" role="dialog" aria-label={t("pensionOverzichten.velden.titel")}>
          <strong>{t("pensionOverzichten.velden.titel")}</strong>
          <p className="po-zacht">{t(`pensionOverzichten.velden.uitleg.${groeperen}`)}</p>
          <div className="po-velden__lijst">
            {VELDEN.map((v) => (
              <label key={v}>
                <input type="checkbox" checked={aan.has(v)} onChange={(e) => zet(v, e.target.checked)} />
                {t(`pensionOverzichten.veld.${v}`)}
              </label>
            ))}
          </div>
          <button type="button" className="po-link" onClick={() => onChange(STANDAARD_VELDEN[groeperen])}>
            {t("pensionOverzichten.velden.standaard")}
          </button>
        </div>
      )}
    </span>
  );
}
